import { Component, lazy, Suspense, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2, FileQuestion } from "lucide-react";
import { toast } from "sonner";
import { downloadFile, fetchFileBlob, fmtSize, previewType } from "@/lib/files";

const ModelViewer = lazy(() => import("@/components/ModelViewer"));
const MIME = { pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg" };

// Fängt Fehler im 3D-Viewer (z. B. Laden des Viewer-Moduls) ab → Fallback mit Download
class ViewerBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onFail("Der 3D-Viewer konnte nicht geladen werden."); }
  render() { return this.state.failed ? null : this.props.children; }
}

function PreviewBody({ att }) {
  const type = previewType(att.ext);
  const [data, setData] = useState({ loading: type !== "none", error: "", url: "", buffer: null });
  const [failed, setFailed] = useState("");

  useEffect(() => {
    if (type === "none") return undefined;
    let alive = true, url = "";
    fetchFileBlob(att).then(async (blob) => {
      if (!alive) return;
      if (type === "model") setData({ loading: false, error: "", url: "", buffer: await blob.arrayBuffer() });
      else {
        url = URL.createObjectURL(new Blob([blob], { type: MIME[att.ext] || blob.type }));
        setData({ loading: false, error: "", url, buffer: null });
      }
    }).catch(() => alive && setData({ loading: false, error: "Die Datei konnte nicht geladen werden.", url: "", buffer: null }));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [att, type]);

  if (type === "none") return <NoPreview att={att} />;
  if (failed) return <NoPreview att={att} reason={failed} />;
  if (data.loading) return <div className="h-full flex items-center justify-center gap-2 text-sm text-slate-600" data-testid="file-preview-loading"><Loader2 className="w-5 h-5 animate-spin" /> Datei wird geladen…</div>;
  if (data.error) return <NoPreview att={att} reason={data.error} />;
  if (type === "image") return <div className="h-full flex items-center justify-center bg-slate-100 rounded-md overflow-auto"><img src={data.url} alt={att.original_name} onError={() => setFailed("Das Bild konnte nicht angezeigt werden (Datei beschädigt oder kein gültiges Bildformat).")} className="max-w-full max-h-full object-contain" data-testid="file-preview-image" /></div>;
  if (type === "pdf") return <iframe src={data.url} title={att.original_name} className="w-full h-full rounded-md border border-slate-200 bg-white" data-testid="file-preview-pdf" />;
  return (
    <ViewerBoundary onFail={setFailed}>
      <Suspense fallback={<div className="h-full flex items-center justify-center text-sm text-slate-600"><Loader2 className="w-5 h-5 animate-spin mr-2" /> 3D-Viewer wird geladen…</div>}>
        <ModelViewer buffer={data.buffer} ext={(att.ext || "").toLowerCase()} onFail={setFailed} />
      </Suspense>
    </ViewerBoundary>
  );
}

function NoPreview({ att, reason = "" }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-3 text-center p-6" data-testid="file-preview-unsupported">
      <FileQuestion className="w-10 h-10 text-slate-400" />
      <h3 className="text-base font-semibold text-slate-900">Vorschau nicht verfügbar</h3>
      {reason
        ? <p className="text-sm text-rose-700 max-w-md" data-testid="file-preview-fail-reason">{reason}</p>
        : <p className="text-sm text-slate-700 max-w-md">Für Dateien vom Typ <span className="font-mono">.{att.ext}</span> ist keine Vorschau verfügbar.</p>}
      <p className="text-sm text-slate-600 max-w-md">Sie können die Datei herunterladen und mit einem passenden Programm öffnen.</p>
      <Button onClick={() => downloadFile(att).catch(() => toast.error("Download fehlgeschlagen."))} data-testid="file-preview-fallback-download"><Download className="w-4 h-4 mr-2" /> Herunterladen</Button>
    </div>
  );
}

// Vorschau-Dialog für PDF, Bilder und 3D-Modelle (STL, 3MF, STEP/STP, OBJ, GLTF/GLB)
export function FilePreviewDialog({ att, onClose }) {
  return (
    <Dialog open={!!att} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw] h-[85vh] flex flex-col gap-3" data-testid="file-preview-dialog">
        {att && (
          <>
            <DialogHeader className="pr-8">
              <DialogTitle className="truncate" title={att.original_name} data-testid="file-preview-title">{att.original_name}</DialogTitle>
              <DialogDescription className="flex items-center gap-3 flex-wrap">
                <span>{fmtSize(att.size)} · {(att.ext || "").toUpperCase()}{att.version > 1 ? ` · Version ${att.version}` : ""}</span>
                <Button size="sm" variant="outline" className="h-7" onClick={() => downloadFile(att).catch(() => toast.error("Download fehlgeschlagen."))} data-testid="file-preview-download">
                  <Download className="w-3.5 h-3.5 mr-1" /> Herunterladen
                </Button>
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 min-h-0"><PreviewBody key={att.id} att={att} /></div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
